import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { AttenteValidation, DemanderValidation, ValiderEmettre, ValideePar } from '../components/ValidationEmission';
import { invoiceAPI } from '../services/api';
import { apiError, dateFr, dateHeureFr, fcfa, today, INVOICE_STATUS, PAYMENT_MODES } from '../utils/format';

const Facture = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canCompta } = useAuth();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');
  const location = useLocation();
  const [message, setMessage] = useState(location.state?.info || '');
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    invoiceAPI.get(id).then((r) => setInv(r.data)).catch((err) => setError(apiError(err)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, after) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const r = await fn();
      setMessage(r.data.message);
      if (after) after(r); else load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!inv) return <Layout title="Facture">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;

  const st = INVOICE_STATUS[inv.statut];
  return (
    <Layout title={inv.numero ? `Facture ${inv.numero}` : 'Facture (brouillon)'}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={st.label} color={st.color} />
        {inv.en_retard && <Chip label="En retard" color="error" variant="outlined" />}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={() => window.open(`/factures/${id}/imprimer`, '_blank')}>
          {inv.statut === 'BROUILLON' ? 'Aperçu' : 'Imprimer / PDF'}
        </Button>
        {inv.statut === 'BROUILLON' && (
          <>
            {canCompta && <Button variant="outlined" onClick={() => navigate(`/factures/${id}/modifier`)}>Modifier</Button>}
            {canCompta && <Button color="error" onClick={() => window.confirm('Supprimer ce brouillon ?') && run(() => invoiceAPI.remove(id), () => navigate('/factures'))}>Supprimer</Button>}
            <DemanderValidation kind="invoices" id={id} doc={inv} onDone={(m, sev) => { if (sev === 'error') setError(m); else { setMessage(m); load(); } }} />
            <ValiderEmettre label="la facture" busy={busy} onEmit={() => run(() => invoiceAPI.emit(id))} />
          </>
        )}
        {inv.statut === 'EMISE' && canCompta && (
          <>
            <Button variant="contained" color="success"
              onClick={() => setPayment({ date_paiement: today(), montant: inv.net_a_payer, mode: 'VIREMENT', reference: '' })}>
              Enregistrer un paiement
            </Button>
            {inv.payments.length === 0 && (
              <Button color="error" onClick={() => {
                const motif = window.prompt('Motif de l\'annulation :');
                if (motif) run(() => invoiceAPI.cancel(id, motif));
              }}>Annuler la facture</Button>
            )}
          </>
        )}
      </Box>

      {inv.statut === 'BROUILLON' && <AttenteValidation kind="invoices" doc={inv} />}
      {inv.statut !== 'BROUILLON' && <ValideePar doc={inv} />}
      {inv.statut === 'ANNULEE' && <Alert severity="error" sx={{ mb: 2 }}>Annulée le {dateHeureFr(inv.annulee_le)} — motif : {inv.motif_annulation}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline">Client</Typography>
            <Typography variant="h6">{inv.client_nom}</Typography>
            <Typography>{inv.client_adresse}</Typography>
            <Typography>{inv.client_ville}</Typography>
            {inv.client_ninea && <Typography variant="body2">NINEA : {inv.client_ninea}</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline">Facture</Typography>
            <Typography>Date : <strong>{dateFr(inv.date_facture)}</strong> — Échéance : <strong>{dateFr(inv.date_echeance)}</strong></Typography>
            {inv.objet && <Typography>Objet : {inv.objet}</Typography>}
            {inv.bons_livraison.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {inv.bons_livraison.map((b) => (
                  <Chip key={b.id} size="small" label={b.bl_numero} onClick={() => navigate(`/bons-livraison/${b.id}`)} />
                ))}
              </Box>
            )}
            {inv.emise_le && <Typography variant="body2" color="text.secondary">Émise le {dateHeureFr(inv.emise_le)} par {inv.emise_par_username}</Typography>}
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell><TableCell>Désignation</TableCell><TableCell align="right">Qté</TableCell>
              <TableCell align="right">Prix HT</TableCell><TableCell align="right">TVA</TableCell><TableCell align="right">Total TTC</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inv.lines.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.code}</TableCell><TableCell>{l.designation}</TableCell>
                <TableCell align="right">{l.quantite}</TableCell><TableCell align="right">{fcfa(l.prix_unitaire_ht)}</TableCell>
                <TableCell align="right">{l.taux_tva}%</TableCell><TableCell align="right">{fcfa(l.total_ttc)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, flexWrap: 'wrap', mb: 3 }}>
        {inv.montant_remise > 0 && (
          <>
            <Typography>Montant brut HT <strong>{fcfa(inv.total_brut_ht)}</strong></Typography>
            <Typography color="error.main">Remise {String(inv.remise_pct).replace('.', ',')} % <strong>−{fcfa(inv.montant_remise)}</strong></Typography>
          </>
        )}
        <Typography>Total HT <strong>{fcfa(inv.total_ht)}</strong></Typography>
        <Typography>Total TVA <strong>{fcfa(inv.total_tva)}</strong></Typography>
        <Typography>Total TTC <strong>{fcfa(inv.total_ttc)}</strong></Typography>
        <Typography>Total réglé <strong>{fcfa(inv.total_regle)}</strong></Typography>
        <Typography variant="h6">Net à payer {fcfa(inv.net_a_payer)} FCFA</Typography>
      </Box>

      {inv.statut !== 'BROUILLON' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline">Paiements</Typography>
          {inv.payments.length === 0 ? <Typography color="text.secondary">Aucun paiement enregistré</Typography> : (
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Date</TableCell><TableCell>Mode</TableCell><TableCell>Référence</TableCell><TableCell align="right">Montant</TableCell><TableCell>Saisi par</TableCell><TableCell /></TableRow>
              </TableHead>
              <TableBody>
                {inv.payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{dateFr(p.date_paiement)}</TableCell><TableCell>{PAYMENT_MODES[p.mode]}</TableCell>
                    <TableCell>{p.reference}</TableCell><TableCell align="right">{fcfa(p.montant)}</TableCell>
                    <TableCell>{p.created_by_username}</TableCell>
                    <TableCell>
                      <IconButton size="small" disabled={busy || !canCompta}
                        onClick={() => window.confirm('Supprimer ce paiement ?') && run(() => invoiceAPI.deletePayment(id, p.id))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Dialog open={!!payment} onClose={() => setPayment(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Paiement — reste {fcfa(inv.net_a_payer)} FCFA</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {payment && (
            <>
              <TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
                value={payment.date_paiement} onChange={(e) => setPayment({ ...payment, date_paiement: e.target.value })} />
              <TextField fullWidth size="small" type="number" label="Montant (FCFA)" sx={{ mb: 2 }}
                value={payment.montant} onChange={(e) => setPayment({ ...payment, montant: e.target.value })} />
              <TextField select fullWidth size="small" label="Mode" sx={{ mb: 2 }}
                value={payment.mode} onChange={(e) => setPayment({ ...payment, mode: e.target.value })}>
                {Object.entries(PAYMENT_MODES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" label="Référence (n° virement, chèque…)"
                value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayment(null)}>Fermer</Button>
          <Button variant="contained" disabled={busy}
            onClick={() => run(() => invoiceAPI.addPayment(id, { ...payment, montant: parseInt(payment.montant, 10) }), () => { setPayment(null); load(); })}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Facture;
